import { useState } from "react";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { Btn } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Logo } from "../components/Logo";
import { auth, googleProvider } from "../services/firebase";
import { authService } from "../services/api";
import { useResponsive } from "../hooks/useResponsive";

const SESSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function mapFirebaseAuthMessage(error) {
  const code = String(error?.code || "");
  if (code.includes("user-not-found")) return "User not found.";
  if (code.includes("invalid-credential") || code.includes("wrong-password") || code.includes("invalid-login-credentials")) {
    return "Invalid credentials.";
  }
  if (code.includes("too-many-requests")) return "Too many attempts. Please try again later.";
  if (code.includes("network-request-failed")) return "Network error. Please check your connection.";
  return "Sign in failed. Please try again.";
}

function rememberSessionWindow() {
  localStorage.setItem("authSessionExpiresAt", String(Date.now() + SESSION_WINDOW_MS));
}

function AuthShell({ children }) {
  const { isMobile } = useResponsive();
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(59,130,246,0.2), transparent 34%), linear-gradient(180deg, #07111f 0%, #0f172a 55%, #090d18 100%)",
        display: "flex",
        fontFamily: "'Space Grotesk','Space Mono',system-ui",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 18 : 40,
      }}
    >
      <div style={{ width: "100%", maxWidth: isMobile ? "100%" : 440 }}>{children}</div>
    </div>
  );
}

const cardStyle = {
  background: "rgba(8,15,28,0.76)",
  border: "1px solid rgba(148,163,184,0.14)",
  borderRadius: 24,
  padding: 32,
  boxShadow: "0 24px 80px rgba(2,6,23,0.45)",
  backdropFilter: "blur(18px)",
  animation: "hexvaultFadeUp 450ms ease",
};

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        background: "rgba(239,68,68,0.12)",
        border: "1px solid rgba(239,68,68,0.28)",
        borderRadius: 12,
        padding: "12px 14px",
        color: "#fca5a5",
        fontSize: 13,
        marginBottom: 16,
        whiteSpace: "pre-wrap",
      }}
    >
      {message}
    </div>
  );
}

function AuthProgress({ title, subtitle }) {
  return (
    <div className="hexvault-auth-progress">
      <div className="hexvault-auth-progress__pulse" />
      <div className="hexvault-auth-progress__content">
        <div className="hexvault-auth-progress__title">{title}</div>
        <div className="hexvault-auth-progress__subtitle">{subtitle}</div>
      </div>
    </div>
  );
}

export function SignIn({ onNav, authSyncing = false }) {
  const { isMobile } = useResponsive();
  const [form, setForm] = useState(() => ({
    email: "",
    password: "",
    remember: localStorage.getItem("hexvaultRememberMe") === "true",
  }));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const busy = loading || redirecting || authSyncing;

  const set = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (busy) return;
    if (!form.email || !form.password) {
      setError("Please enter your email and password.");
      return;
    }

    setLoading(true);
    setRedirecting(false);
    setError("");
    try {
      await setPersistence(auth, form.remember ? browserLocalPersistence : browserSessionPersistence);
      localStorage.setItem("hexvaultRememberMe", form.remember ? "true" : "false");
      rememberSessionWindow();
      const credential = await signInWithEmailAndPassword(auth, form.email, form.password);
      if (!credential.user.emailVerified) {
        await signOut(auth);
        setRedirecting(false);
        setError("Please verify your email before signing in.");
        return;
      }
      setRedirecting(true);
    } catch (err) {
      try {
        const fallback = await authService.loginWithPassword({
          email: form.email,
          password: form.password,
          remember: form.remember,
        });
        rememberSessionWindow();
        localStorage.setItem("authToken", fallback.data.token);
        localStorage.setItem("user", JSON.stringify(fallback.data.user));
        localStorage.setItem("authMode", "custom");
        setRedirecting(true);
        window.dispatchEvent(
          new CustomEvent("hexvault:custom-login", {
            detail: {
              token: fallback.data.token,
              user: fallback.data.user,
            },
          })
        );
      } catch (fallbackError) {
        setRedirecting(false);
        setError(fallbackError.response?.data?.message || mapFirebaseAuthMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (busy) return;
    setLoading(true);
    setRedirecting(true);
    setError("");
    try {
      await setPersistence(auth, form.remember ? browserLocalPersistence : browserSessionPersistence);
      localStorage.setItem("hexvaultRememberMe", form.remember ? "true" : "false");
      rememberSessionWindow();
      googleProvider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(mapFirebaseAuthMessage(err));
      setLoading(false);
      setRedirecting(false);
    }
  };

  return (
    <AuthShell>
      <div style={{ ...cardStyle, position: "relative", overflow: "hidden", padding: isMobile ? 22 : 32, borderRadius: isMobile ? 20 : 24 }}>
        {busy && (
          <AuthProgress
            title={redirecting ? "Opening your vault" : "Signing you in"}
            subtitle={redirecting ? "Securing your session and preparing your dashboard." : "Checking your credentials and starting a secure session."}
          />
        )}
        <div style={{ marginBottom: 28 }}>
          <Logo onClick={() => onNav("landing")} />
          <h1 style={{ fontSize: 30, fontWeight: 800, marginBottom: 6, marginTop: 24, letterSpacing: "-0.04em", color: "#f8fafc" }}>
            Welcome back
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>
            Sign in to HexVault to manage your storage securely.
          </p>
        </div>
        <ErrorBanner message={error} />
        {redirecting && (
          <div style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(96,165,250,0.28)", borderRadius: 12, padding: "12px 14px", color: "#bfdbfe", fontSize: 13, marginBottom: 16 }}>
            Login successful. Redirecting you to your vault...
          </div>
        )}
        <form onSubmit={handleSubmit} className={busy ? "hexvault-auth-form hexvault-auth-form--busy" : "hexvault-auth-form"}>
          <Input label="Email" type="email" placeholder="you@hexvault.in" value={form.email} onChange={set("email")} disabled={busy} />
          <Input label="Password" type="password" placeholder="Your password" value={form.password} onChange={set("password")} disabled={busy} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 12 : 0, marginBottom: 24, marginTop: -8 }}>
            <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: busy ? "default" : "pointer", fontSize: 13, color: "#94a3b8", userSelect: "none" }}>
              <input type="checkbox" checked={form.remember} onChange={set("remember")} disabled={busy} style={{ position: "absolute", opacity: 0, pointerEvents: "none" }} />
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  border: form.remember ? "1px solid rgba(96,165,250,0.65)" : "1px solid rgba(148,163,184,0.28)",
                  background: form.remember ? "linear-gradient(135deg, rgba(59,130,246,0.95), rgba(52,211,153,0.82))" : "rgba(255,255,255,0.03)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: form.remember ? "0 0 0 4px rgba(59,130,246,0.12)" : "none",
                  transition: "all 180ms ease",
                  flexShrink: 0,
                }}
              >
                <span style={{ color: "#eff6ff", fontSize: 11, fontWeight: 900, opacity: form.remember ? 1 : 0, transform: form.remember ? "scale(1)" : "scale(0.6)", transition: "all 180ms ease" }}>
                  ✓
                </span>
              </span>
              Remember me
            </label>
            <button type="button" onClick={() => onNav("forgot")} style={linkStyle} disabled={busy}>
              Forgot password?
            </button>
          </div>
          <Btn type="submit" size="lg" style={{ width: "100%", marginBottom: 10 }} disabled={busy}>
            {loading ? "Signing in..." : redirecting ? "Redirecting..." : "Sign In"}
          </Btn>
        </form>
        <Btn variant="ghost" size="lg" style={{ width: "100%", border: "1px solid rgba(255,255,255,0.12)" }} onClick={handleGoogle} disabled={busy}>
          Continue with Google
        </Btn>
        <p style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginTop: 20 }}>
          No account?{" "}
          <button onClick={() => onNav("signup")} style={linkStyle}>
            Sign Up Free
          </button>
        </p>
      </div>
    </AuthShell>
  );
}

export function ForgotPassword({ onNav }) {
  const { isMobile } = useResponsive();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!email) {
      setError("Enter your email to continue.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to send reset email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div style={{ ...cardStyle, padding: isMobile ? 22 : 32, borderRadius: isMobile ? 20 : 24 }}>
        <Logo onClick={() => onNav("landing")} />
        <h1 style={{ fontSize: 24, fontWeight: 800, marginTop: 20, marginBottom: 6, letterSpacing: "-0.04em", color: "#f8fafc" }}>
          Reset your password
        </h1>
        <ErrorBanner message={error} />
        {!sent ? (
          <>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 24 }}>
              Enter your email and we&apos;ll send a reset link.
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                handleSend();
              }}
            >
              <Input label="Email" type="email" placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.target.value)} disabled={loading} />
              <Btn type="submit" size="lg" style={{ width: "100%" }} disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Btn>
            </form>
          </>
        ) : (
          <>
            <div
              style={{
                background: "rgba(34,197,94,0.1)",
                border: "1px solid rgba(34,197,94,0.3)",
                borderRadius: 10,
                padding: "16px",
                marginTop: 16,
                marginBottom: 20,
                textAlign: "center",
                color: "#86efac",
              }}
            >
              Check your inbox for reset instructions.
            </div>
            <Btn variant="ghost" style={{ width: "100%", justifyContent: "center" }} onClick={() => setSent(false)}>
              Resend email
            </Btn>
          </>
        )}
        <p style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginTop: 20 }}>
          <button onClick={() => onNav("signin")} style={linkStyle}>
            Back to Sign In
          </button>
        </p>
      </div>
    </AuthShell>
  );
}

const linkStyle = {
  background: "none",
  border: "none",
  color: "#60a5fa",
  cursor: "pointer",
  fontWeight: 600,
};
